import { useEffect, useRef, useState } from "react";
import { MapPin, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const getPos = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("no geolocation"));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve([p.coords.latitude, p.coords.longitude]),
      (e) => reject(e),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  });

const mmss = (mins) => {
  const t = Math.max(0, Math.round(mins * 60));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
};

// One-tap class check-in on a Today plan leg. Driven by Dashboard's shared active state.
export default function ClassCheckinButton({ buildingId, name, active, onStarted, onEnded }) {
  const [elapsed, setElapsed] = useState(0);
  const [busy, setBusy] = useState(false);
  const tick = useRef(null);

  const isThis = active && active.kind === "class" && active.place_id === buildingId;
  const isOther = active && !isThis;

  useEffect(() => {
    if (!isThis) { clearInterval(tick.current); return; }
    const started = new Date(active.started_at).getTime();
    const upd = () => setElapsed((Date.now() - started) / 60000);
    upd();
    tick.current = setInterval(upd, 1000);
    return () => clearInterval(tick.current);
  }, [isThis, active]);

  if (isOther) return null;
  const ready = isThis && elapsed >= (active.min_minutes || 30);

  const start = async () => {
    setBusy(true);
    try {
      const l = await getPos();
      const r = await api.post("/checkins/start", { kind: "class", place_id: buildingId, lat: l[0], lng: l[1] });
      onStarted?.(r.data);
      toast.success(`Checked in for ${name}. Stay ${r.data.min_minutes} min to earn ${r.data.points_preview} pts.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not check in."); }
    finally { setBusy(false); }
  };

  const checkout = async () => {
    setBusy(true);
    try {
      const l = await getPos();
      const r = await api.post(`/checkins/${active.id}/complete`, { lat: l[0], lng: l[1] });
      if (r.data.awarded) {
        toast.success(`+${r.data.points} pts for ${r.data.place_name}!`);
        if (r.data.streak_bonus > 0) toast.success(`🔥 +${r.data.streak_bonus} streak bonus!`);
        (r.data.new_badges || []).forEach((b) => toast.success(`🏅 New badge: ${b.name}!`, { duration: 6000 }));
        onEnded?.();
      } else {
        toast.message(`Stay ${r.data.remaining_min} more min to earn points.`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not check out."); }
    finally { setBusy(false); }
  };

  if (isThis) {
    return (
      <button onClick={checkout} disabled={!ready || busy} data-testid={`leg-checkout-${buildingId}`}
        className={`shrink-0 flex items-center gap-1 rounded-md px-2 py-1 mono text-xs font-bold transition
          ${ready ? "bg-[#166534] text-white hover:opacity-90" : "bg-secondary text-muted-foreground cursor-not-allowed"}`}>
        {busy ? <Loader2 className="animate-spin" size={12} /> : <CheckCircle2 size={12} strokeWidth={2} />}
        {ready ? "Check out" : mmss(elapsed)}
      </button>
    );
  }
  return (
    <button onClick={start} disabled={busy} data-testid={`leg-checkin-${buildingId}`}
      title="Check in to earn points"
      className="shrink-0 flex items-center gap-1 rounded-md px-2 py-1 mono text-xs font-bold bg-secondary text-brand hover:bg-brand hover:text-white transition">
      {busy ? <Loader2 className="animate-spin" size={12} /> : <MapPin size={12} strokeWidth={2} />}
      Check in
    </button>
  );
}
